import React, { useState } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import Loader from '../Loader';
import LoaderLight from '../LoaderLight';
import Dialog from '../Dialog';
import Container from '../Container';
import Text from '../Text';
import SelectButton from '../SelectButton';
import TextArea from '../TextArea';
import Uploader from '../Uploader';
import Image from '../Image';
import EmptyButton from '../EmptyButton';
import AlertDialog from '../AlertDialog';
import Validator from '../Validator';
import Form from '../Form';
import CloseIcon from './closeIcon';
import PaperClipIcon from './paperClipIcon';
import feedbackSuccess from '../../images/feedbackSuccess.svg';

const pardotFeedbackMutation = gql`
  mutation PardotFeedback($data: JSONObject!) {
    pardotFeedback(data: $data) {
      status
    }
  }
`;

const createFeedbackMutation = gql`
  mutation createFeedback($input: CreateFeedbackInput!) {
    createFeedback(input: $input) {
      feedback {
        name
      }
    }
  }
`;

const uploadErrorMessages = {
  maxUploads: `The maximum number of images you're allowed to upload is 3. `,
  maxFileSize: `Oops, your file size is too big. Please try again ensuring the file is a maximum of 10MB. `,
  fileType:
    "Oops, you've selected an invalid file format. You can try again with a .jpg, .jpeg or .png file. ",
  upload: 'Oops, something went wrong.',
};

const defaultTypes = [
  {
    label: 'Incorrect Details',
  },
  {
    label: 'Missing Business',
  },
  {
    label: 'Technical Issue',
  },
  {
    label: 'Suggestion',
  },
];

const schema = Joi.object({
  type: Joi.string().required().messages({
    'string.empty': 'feedback type is required',
  }),
  message: Joi.string().required().messages({
    'string.empty': 'message is required',
  }),
});

const FeedbackForm = ({
  show,
  mode,
  onClose,
  application,
  types,
  formTitle,
  formSubheading,
  notificationSliderProps,
  name,
  email,
  ...props
}) => {
  const [assets, setAssets] = useState([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const client = useApolloClient();

  const {
    register,
    formState: { errors, isDirty, isValid, isSubmitting },
    handleSubmit,
    control,
    watch,
    reset,
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      type: '',
      message: '',
    },
    resolver: joiResolver(schema),
  });

  const onSubmit = async values => {
    const { type, message } = values;
    await client.mutate({
      mutation: createFeedbackMutation,
      variables: {
        input: {
          type,
          name,
          email,
          message,
          application,
          assets: assets.length ? assets : null,
          url: window.location.href,
        },
      },
    });
    await client.mutate({
      mutation: pardotFeedbackMutation,
      variables: {
        data: {
          enquiry_reason: type,
          message,
          name,
          email,
        },
      },
    });
    reset();
    setAssets([]);
    onClose();
    setShowSuccess(true);
  };

  const onUploadError = err => {
    if (err.error) {
      setUploadErrorMessage(
        uploadErrorMessages[err.error] || 'Oops, something went wrong.'
      );
    }
  };

  const onRemoveImageClick = (e, i) => {
    e.preventDefault();
    setAssets(assets.filter((asset, index) => i !== index));
  };

  const onUploadSuccess = files => {
    if (assets.length === 3) {
      setUploadErrorMessage(uploadErrorMessages.maxUploads);
      return;
    }
    setAssets([...files.map(({ src }) => src), ...assets]);
    setUploadErrorMessage('');
  };
  return (
    <>
      <Dialog
        zIndex="99999999"
        show={show}
        maxWidth="408px"
        border="none"
        mode={mode}
        borderRadius={mode === 'mobile' ? '0' : '20px'}
        {...props}
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Container
            padding="30px 30px 15px 30px"
            color="black"
            fontSize="25px"
            fontWeight="600"
            borderTopLeftRadius="20px"
            borderTopRightRadius="20px"
            display="flex"
            justifyContent="space-between"
          >
            <Text fontSize="25px" color="#4a4a4a" fontWeight="700">
              {formTitle}
            </Text>
            <EmptyButton
              onClick={() => {
                if (isDirty) {
                  setShowAlertDialog(true);
                } else {
                  setAssets([]);
                  onClose();
                }
              }}
              display="flex"
              alignItems="center"
            >
              <CloseIcon />
            </EmptyButton>
          </Container>
          <Container padding="30px">
            <Text
              display="block"
              marginBottom="10px"
              fontSize="16px"
              color="normal"
              fontWeight="600"
              lineHeight="1.2"
            >
              {formSubheading}
              <Text color="red"> *</Text>
            </Text>
            <Container display="flex" flexWrap="wrap" margin="0 -3px 15px -3px">
              {types.map(item => (
                <Controller
                  key={item.label}
                  control={control}
                  name="type"
                  render={({ field: { onChange, value } }) => (
                    <SelectButton
                      onClick={() => onChange(item.label)}
                      selected={value === item.label}
                      margin="3px"
                    >
                      {item.icon && (
                        <Text
                          display="flex"
                          width="20px"
                          alignItems="center"
                          justifyContent="center"
                          marginRight="5px"
                        >
                          {typeof item.icon === 'function'
                            ? item.icon(value === item.label)
                            : item.icon}
                        </Text>
                      )}
                      {item.label}
                    </SelectButton>
                  )}
                />
              ))}
              <Container marginBottom="15px">
                <Validator>{errors?.type?.message}</Validator>
              </Container>
            </Container>
            <Text
              display="block"
              marginBottom="10px"
              fontSize="16px"
              color="normal"
              fontWeight="600"
              lineHeight="1.2"
            >
              Message<Text color="red"> *</Text>
            </Text>
            <TextArea
              fontFamily="Circular"
              {...register('message', { required: true, max: 1000 })}
              placeholder="We value your input and would love to hear from you..."
              maxLength={1000}
              minWidth="100%"
              maxWidth="100%"
              backgroundColor="#f8f8f8"
              boxShadow="none"
              border="none"
              _placeholder={{
                color: 'text300',
              }}
              _focus={{
                border: '1px solid #97AEFF',
              }}
            />
            <Container display="flex" justifyContent="flex-end">
              <Text fontSize="12px" color="normal">
                {watch('message').length}
                /1000
              </Text>
            </Container>
            <Container marginBottom="15px">
              <Validator>{errors?.message?.message}</Validator>
            </Container>
            <Text
              display="block"
              lineHeight="1.2"
              fontSize="16px"
              color="normal"
              fontWeight="600"
              marginBottom="5px"
            >
              Have a photo to support your suggestion?
            </Text>
            <Uploader
              onUploadSuccess={onUploadSuccess}
              onError={onUploadError}
              name="assets"
              maxUploads={3}
              multiple
            >
              <Container display="flex" flexWrap="wrap" margin="0 -5px">
                <Container
                  borderRadius={assets.length ? '5px' : '30px'}
                  width={assets.length ? '60px' : 'auto'}
                  height={assets.length ? '60px' : '32px'}
                  border={theme => `1px solid ${theme.primary.primary200}`}
                  margin="5px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  position="relative"
                  padding={assets.length ? 0 : '10px 15px'}
                >
                  <PaperClipIcon />
                  {!assets.length ? (
                    <Text
                      marginLeft="5px"
                      color="text700"
                      fontSize="14px"
                      fontWeight="500"
                    >
                      Choose file
                    </Text>
                  ) : null}
                </Container>
                {assets.map((src, i) => (
                  <Container
                    key={src}
                    flex="0 0 60px"
                    height="60px"
                    margin="5px"
                    position="relative"
                  >
                    <EmptyButton
                      onClick={e => onRemoveImageClick(e, i)}
                      position="absolute"
                      right="-16px"
                      marginTop="-16px"
                      padding="8px"
                    >
                      <Text
                        border="1px solid white"
                        borderRadius="100%"
                        width="22px"
                        height="22px"
                        backgroundColor="rgba(0,0,0,0.6)"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <CloseIcon fill="white" />
                      </Text>
                    </EmptyButton>
                    <Container
                      width="100%"
                      height="100%"
                      border={theme => `1px solid ${theme.primary.primary200}`}
                      borderRadius="5px"
                      overflow="hidden"
                    >
                      <Image
                        height="100%"
                        width="100%"
                        objectFit="cover"
                        src={src}
                      />
                    </Container>
                  </Container>
                ))}
              </Container>
            </Uploader>
            <Validator show={!!uploadErrorMessage}>
              {uploadErrorMessage}
            </Validator>

            {mode !== 'desktop' ? (
              <EmptyButton
                type="submit"
                disabled={!isValid}
                height="44px"
                width="90%"
                fontSize="16px"
                position="fixed"
                left="50%"
                borderRadius="100px"
                transform="translateX(-50%)"
                bottom="25px"
                color="white"
                backgroundColor="primary"
                fontWeight="600"
                _disabled={{
                  backgroundColor: '#D9D9D9',
                  color: 'white',
                  border: 'none',
                }}
                _hover={{
                  backgroundColor:
                    'linear-gradient(180deg, #98B0FD -45.26%, #2D60FB 100%)',
                  boxShadow: 'none',
                  border: 'none',
                  color: isValid ? 'primary' : '#757575',
                }}
              >
                {isSubmitting && (
                  <Container
                    height="100%"
                    width="100%"
                    backgroundColor="primary"
                    left="0"
                    top="0"
                    position="absolute"
                    borderRadius="100px"
                  >
                    <LoaderLight
                      position="absolute"
                      transform="translate(-50%,-50%)"
                      top="55%"
                      left="50%"
                      width="130px"
                    />
                  </Container>
                )}
                Submit
              </EmptyButton>
            ) : (
              <Container
                display="flex"
                justifyContent="flex-end"
                marginTop="50px"
              >
                <EmptyButton
                  position="relative"
                  disabled={!isValid}
                  fontSize="16px"
                  fontWeight="600"
                  padding="0"
                  width="auto"
                  type="submit"
                  default
                  rounded
                  backgroundColor="white"
                  color="primary"
                  border="none"
                  display="flex"
                  justifyContent="end"
                  alignItems="center"
                  _disabled={{
                    backgroundColor: 'white',
                    color: '#757575',
                    border: 'none',
                  }}
                  _hover={{
                    backgroundColor: 'white',
                    boxShadow: 'none',
                    border: 'none',
                    color: isValid ? 'primary' : '#757575',
                  }}
                >
                  {isSubmitting && (
                    <Container
                      height="100%"
                      width="100%"
                      backgroundColor="white"
                      left="0"
                      top="0"
                      position="absolute"
                    >
                      <Loader
                        position="absolute"
                        transform="translate(-50%,-50%)"
                        top="50%"
                        left="50%"
                        width="130px"
                      />
                    </Container>
                  )}
                  Submit
                </EmptyButton>
              </Container>
            )}
          </Container>
        </Form>
      </Dialog>
      <Dialog
        show={showSuccess}
        maxWidth="408px"
        mode={mode}
        clickOutside
        onClose={() => setShowSuccess(false)}
      >
        <Container
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="30px"
          flexDirection="column"
          height={mode === 'mobile' ? '100%' : undefined}
        >
          <Container
            width="100%"
            marginBottom="20px"
            display="flex"
            justifyContent="end"
          >
            <EmptyButton
              onClick={() => setShowSuccess(false)}
              display="flex"
              alignItems="center"
            >
              <CloseIcon width="20px" fill="#757575" />
            </EmptyButton>
          </Container>
          <Container display="flex" alignItems="center" flexDirection="column">
            <Image src={feedbackSuccess} />
            <Text fontSize="26px" fontWeight="700">
              Feedback Sent!
            </Text>
            <Container margin="15px 0 40px 0">
              <Text
                display="flex"
                textAlign="center"
                fontSize="16px"
                fontWeight="450"
              >
                Thank you for sharing your feedback with us. Stay connected with
                us for updates and improvements.
              </Text>
            </Container>
          </Container>
          <EmptyButton
            color="primary"
            fontSize="16px"
            fontWeight="600"
            backgroundColor="#F8F8F8"
            borderRadius="30px"
            padding="10px 20px"
            width={mode === 'mobile' ? '100%' : undefined}
            _hover={{
              backgroundColor: '#ececec',
            }}
            onClick={() => setShowSuccess(false)}
          >
            Done
          </EmptyButton>
        </Container>
      </Dialog>

      <AlertDialog
        zIndex="999999999"
        show={showAlertDialog}
        heading="Are you sure you want to quit?"
        text="If you quit now, this form will reset and you'll have to fill it in again."
        buttons={[
          {
            children: 'Quit',
            props: {
              backgroundColor: 'white',
              minWidth: 'unset',
              width: '115px',
              rounded: true,
              onClick: () => {
                onClose();
                setAssets([]);
                reset();
                setShowAlertDialog(false);
              },
            },
          },
          {
            children: "No, I'm not done yet!",
            props: {
              primary: true,
              rounded: true,
              onClick: () => setShowAlertDialog(false),
            },
          },
        ]}
      />
    </>
  );
};

FeedbackForm.propTypes = {
  show: PropTypes.bool,
  mode: PropTypes.string,
  onClose: PropTypes.func,
  application: PropTypes.string,
  formTitle: PropTypes.string,
  formSubheading: PropTypes.string,
  notificationSliderProps: PropTypes.shape({}),
  types: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    })
  ),
  name: PropTypes.string,
  email: PropTypes.string,
};

FeedbackForm.defaultProps = {
  show: false,
  mode: undefined,
  onClose: () => {},
  application: null,
  formTitle: 'Send Feedback',
  formSubheading: "What's your feedback about?",
  notificationSliderProps: {},
  types: defaultTypes,
  name: '',
  email: '',
};

export default FeedbackForm;
